import {
	ReactElement,
	useState,
	MouseEvent,
	PropsWithChildren
} from "react";
import classNames from "classnames";
import { localize } from "client_legacy/ndaw/culture";
import { IconButton } from "../IconButton/IconButton";
import { setTrackingId } from "client/Misc/utils";
import styles from "./PopoutableContent.module.less";

export type PopoutableContentProps = {
	isPoppedOut: boolean;
	title?: string;
	onPopin(): void;
	trackingId?: string;
};

// The reason why we don't use regular dialogs is mainly because of potential iframes being inside the content
// which needs to be popped out. When an iframe element is moved (basically remove + add) in the DOM it needs
// to create a new window context according to the spec. Doing so results in a reload. Generally we want to
// popout the content without resetting it's state, so a reload is not desirable.
export const PopoutableContent = (props: PropsWithChildren<PopoutableContentProps>): ReactElement => {
	const {
		isPoppedOut,
		title,
		onPopin,
		children,
		trackingId
	} = props;

	const [isEnlarged, setIsEnlarged] = useState<boolean>(false);

	const enlargeHandler = (): void => setIsEnlarged(true);
	const shrinkHandler = (): void => setIsEnlarged(false);

	const contextMenuHandler = (args: MouseEvent): void => {
		// We interrupt the context menu event so it doesn't propagate to the host of the
		// popoutable content (e.g. on the workspace it would result in switching to edit mode)
		args.stopPropagation();
	};

	return (
		<>
			{isPoppedOut && (
				<div
					className={styles.backdrop}
					onContextMenu={contextMenuHandler}
				/>
			)}

			<div
				{...setTrackingId(trackingId)}
				className={classNames(
					styles.root,
					{
						[styles.root_poppedOut_fullScreen]: isPoppedOut && isEnlarged,
						[styles.root_poppedOut]: isPoppedOut,
					}
				)}
				onContextMenu={contextMenuHandler}
			>
				<div className={styles.header}>
					<h2 className={styles.title}>
						{title}
					</h2>
					<div className={styles.actionButtonContainer}>
						{isEnlarged
							? (
								<IconButton
									kind="transparent"
									icon="arrow-down-left-and-arrow-up-right-to-center"
									tooltip={localize("_Restore")}
									onClick={shrinkHandler}
									trackingId="popoutable-content-restore"
								/>
							)
							: (
								<IconButton
									kind="transparent"
									icon="arrow-up-right-and-arrow-down-left-from-center"
									tooltip={localize("_Maximize")}
									onClick={enlargeHandler}
									trackingId="popoutable-content-maximize"
								/>
							)
						}
						<IconButton
							kind="transparent"
							icon="xmark"
							tooltip={localize("_Close")}
							onClick={onPopin}
							trackingId="popoutable-content-close"
						/>
					</div>
				</div >
				<div className={styles.host}>
					{children}
				</div>
			</div>
		</>
	);
};

PopoutableContent.displayName="PopoutableContent";
